"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "./supabase.ts";

export type CreditsGateContextValue = {
  creditsRemaining: number | null;
  isLoadingCredits: boolean;
  /** True when balance is known and remaining credits are below 1 */
  isCreditsLocked: boolean;
  refreshCredits: () => Promise<void>;
};

const CreditsGateContext = createContext<CreditsGateContextValue | null>(null);

export const CreditsGateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);

  const fetchCredits = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setCreditsRemaining(null);
      setIsLoadingCredits(false);
      return;
    }

    setIsLoadingCredits(true);
    try {
      const res = await fetch("/api/billing/subscription", {
        headers: { "user-id": user.id },
      });
      if (!res.ok) {
        setCreditsRemaining(null);
        return;
      }
      const data = await res.json();
      const c = data.credits;
      if (c) {
        const avail =
          (Number(c.total_credits) || 0) - (Number(c.used_credits) || 0);
        setCreditsRemaining(avail);
      } else {
        setCreditsRemaining(0);
      }
    } catch {
      setCreditsRemaining(null);
    } finally {
      setIsLoadingCredits(false);
    }
  }, []);

  useEffect(() => {
    void fetchCredits();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void fetchCredits();
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchCredits]);

  const isCreditsLocked = useMemo(() => {
    if (isLoadingCredits || creditsRemaining === null) return false;
    return creditsRemaining < 1;
  }, [isLoadingCredits, creditsRemaining]);

  const value = useMemo(
    () => ({
      creditsRemaining,
      isLoadingCredits,
      isCreditsLocked,
      refreshCredits: fetchCredits,
    }),
    [creditsRemaining, isLoadingCredits, isCreditsLocked, fetchCredits],
  );

  return (
    <CreditsGateContext.Provider value={value}>
      {children}
    </CreditsGateContext.Provider>
  );
};

export function useCreditsGate(): CreditsGateContextValue {
  const ctx = useContext(CreditsGateContext);
  if (!ctx) {
    throw new Error("useCreditsGate must be used within CreditsGateProvider");
  }
  return ctx;
}

/** Routes that must stay reachable when credits are locked (auth / password recovery). */
const CREDITS_GATE_EXEMPT_PATHS = new Set([
  "/billing",
  "/set-new-password",
  "/password-reset-otp",
  "/reset-password",
  "/check-your-email",
  "/login",
  "/signup",
  "/verify-otp",
  "/otp-verification",
]);

function isCreditsGateExemptPath(pathname: string): boolean {
  return CREDITS_GATE_EXEMPT_PATHS.has(pathname);
}

/** Sends users to /billing when out of credits (except on exempt auth/billing routes). */
export function CreditsRedirectWhenLocked() {
  const { isCreditsLocked, isLoadingCredits } = useCreditsGate();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoadingCredits) return;
    if (!isCreditsLocked) return;
    if (isCreditsGateExemptPath(location.pathname)) return;
    navigate("/billing", { replace: true });
  }, [isCreditsLocked, isLoadingCredits, location.pathname, navigate]);

  return null;
}
